import {
  IDatabaseService,
  Deck,
  Card,
  Session,
  CardPerformance,
  UserCardPreference,
  DeckWithCount,
  SessionSummary,
  CardAttemptStats,
  SessionCardStats,
  CardAttempt,
  ExportData,
  ImportResult,
  ClearProgressResult
} from './IDatabaseService';

/**
 * Electron database adapter that wraps the existing Electron IPC API
 * NO CHANGES to existing Electron code required - this just delegates to window.electronAPI
 */
export class ElectronDatabaseAdapter implements IDatabaseService {
  async initialize(): Promise<void> {
    // Electron DB initializes via IPC on app start
    // Nothing to do here
  }

  async getDecks(): Promise<Deck[]> {
    return window.electronAPI.getDecks();
  }

  async getDeck(id: string): Promise<Deck | null> {
    return window.electronAPI.getDeck(id);
  }

  async getDecksWithCardCounts(): Promise<DeckWithCount[]> {
    return window.electronAPI.getDecksWithCardCounts();
  }

  async createDeck(deck: Omit<Deck, 'id'> & { id?: string }): Promise<Deck> {
    return window.electronAPI.createDeck(deck);
  }

  async updateDeck(id: string, updates: Partial<Deck>): Promise<Deck> {
    return window.electronAPI.updateDeck(id, updates);
  }

  async deleteDeck(id: string): Promise<void> {
    return window.electronAPI.deleteDeck(id);
  }

  async getCards(deckId: string): Promise<Card[]> {
    return window.electronAPI.getCards(deckId);
  }

  async getCard(id: string): Promise<Card | null> {
    return window.electronAPI.getCard(id);
  }

  async createCard(card: Card): Promise<Card> {
    return window.electronAPI.createCard(card);
  }

  async updateCard(id: string, updates: Partial<Card>): Promise<Card> {
    return window.electronAPI.updateCard(id, updates);
  }

  async deleteCard(id: string): Promise<void> {
    return window.electronAPI.deleteCard(id);
  }

  async updateCardOrder(deckId: string, cardIds: string[]): Promise<void> {
    return window.electronAPI.updateCardOrder(deckId, cardIds);
  }

  async getSessions(): Promise<Session[]> {
    return window.electronAPI.getSessions();
  }

  async createSession(session: Session): Promise<Session> {
    return window.electronAPI.createSession(session);
  }

  async updateSession(id: string, updates: Partial<Session>): Promise<Session> {
    return window.electronAPI.updateSession(id, updates);
  }

  async getSessionsWithSummary(): Promise<SessionSummary[]> {
    return window.electronAPI.getSessionsWithSummary();
  }

  async getSessionSummary(sessionId: string): Promise<SessionSummary | null> {
    return window.electronAPI.getSessionSummary(sessionId);
  }

  async getCardPerformance(cardId: string): Promise<CardPerformance[]> {
    return window.electronAPI.getCardPerformance(cardId);
  }

  async createPerformanceRecord(performance: Omit<CardPerformance, 'id'>): Promise<CardPerformance> {
    return window.electronAPI.createPerformanceRecord(performance);
  }

  async getCardAttemptStats(cardId: string): Promise<CardAttemptStats> {
    return window.electronAPI.getCardAttemptStats(cardId);
  }

  async getSessionCardStats(cardId: string, sessionId: string): Promise<SessionCardStats> {
    return window.electronAPI.getSessionCardStats(cardId, sessionId);
  }

  async getSessionCardAttempts(sessionId: string): Promise<CardAttempt[]> {
    return window.electronAPI.getSessionCardAttempts(sessionId);
  }

  async getUserCardPreferences(deckId: string): Promise<UserCardPreference[]> {
    return window.electronAPI.getUserCardPreferences(deckId);
  }

  async getCardPreference(cardId: string, deckId: string): Promise<UserCardPreference | null> {
    return window.electronAPI.getCardPreference(cardId, deckId);
  }

  async updateCardPreference(cardId: string, deckId: string, isExcluded: boolean): Promise<UserCardPreference> {
    return window.electronAPI.updateCardPreference(cardId, deckId, isExcluded);
  }

  async exportData(): Promise<ExportData> {
    // Get export data from Electron's database service
    return window.electronAPI.exportAllData();
  }

  async exportSelectedDecks(deckIds: string[]): Promise<ExportData> {
    // Get export data for selected decks from Electron's database service
    return window.electronAPI.exportSelectedDecks(deckIds);
  }

  async importData(data: ExportData): Promise<ImportResult> {
    // Import data via Electron's database service
    return window.electronAPI.importDataDirect(data);
  }

  async clearProgressData(): Promise<ClearProgressResult> {
    return window.electronAPI.clearProgressData();
  }

  async clearDeckProgress(deckId: string): Promise<{ success: boolean; deletedRecords: number; error?: string }> {
    return window.electronAPI.clearDeckProgress(deckId);
  }

  async getCompletedCardIds(deckId: string): Promise<string[]> {
    return window.electronAPI.getCompletedCardIds(deckId);
  }

  async resetCardProgress(cardId: string): Promise<{ success: boolean; deletedRecords: number; error?: string }> {
    return window.electronAPI.resetCardProgress(cardId);
  }
}
